import { friendlyRunError } from '../utils/modelFailure';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  RefreshCw, Search, Bot, GitBranch, Loader2,
  CheckCircle2, Clock, Square, MessageSquare,
  MoreHorizontal, X, StopCircle, ChevronRight, ThumbsUp, ThumbsDown, Sparkles, FlaskConical, Check, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { agentRunAPI, evalsAPI } from '../services/api';
import EvalsPanel from '../components/missionControl/EvalsPanel.jsx';

// ── Theme ─────────────────────────────────────────────────────────────────────
const T = {
  page:        'var(--lyra-color-surface)',
  surface:     'var(--lyra-color-surface)',
  surface2:    'var(--lyra-color-surface-2)',
  surface3:    'var(--app-surface-3)',
  border:      'var(--lyra-color-border)',
  border2:     'var(--app-border-strong)',
  ink:         'var(--lyra-color-text)',
  ink2:        'var(--app-text-2)',
  ink3:        'var(--lyra-color-text-muted)',
  ink4:        'var(--app-text-3)',
  accent:      'var(--lyra-color-primary)',
  accentLight: 'var(--app-primary-soft)',
  accentMid:   'var(--app-primary-soft-2)',
  accentDark:  'var(--app-primary-deep)',
  green:       'var(--lyra-color-success)',
  greenBg:     'var(--lyra-color-success-bg)',
  greenBd:     'var(--app-success-border)',
  red:         'var(--lyra-color-danger)',
  redBg:       'var(--lyra-color-danger-bg)',
  redBd:       'var(--app-danger-border)',
  amber:       'var(--lyra-color-warning)',
  amberBg:     'var(--lyra-color-warning-bg)',
  amberBd:     'var(--app-warning-border)',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(date) {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
function formatDuration(ms) {
  if (!ms || ms <= 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `0m ${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}
function formatAvg(ms) {
  if (!ms || ms <= 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60 > 0 ? s % 60 + 's' : ''}`.trim();
}

// ── Status badge ──────────────────────────────────────────────────────────────
const STATUS_CFG = {
  running: { dot: 'var(--lyra-color-success)', bg: 'var(--lyra-color-success-bg)', bd: 'var(--app-success-border)', fg: '#15803d', label: 'running'  },
  success: { dot: 'var(--lyra-color-primary)', bg: 'var(--app-primary-soft)', bd: 'var(--app-primary-soft-2)', fg: 'var(--app-primary-deep)', label: 'done'     },
  error:   { dot: 'var(--lyra-color-danger)', bg: 'var(--lyra-color-danger-bg)', bd: 'var(--app-danger-border)', fg: 'var(--lyra-color-danger)', label: 'failed'   },
  stopped: { dot: 'var(--app-text-3)', bg: 'var(--lyra-color-surface-2)', bd: 'var(--lyra-color-border)', fg: 'var(--lyra-color-text-muted)', label: 'stopped'  },
};
function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.stopped;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 99,
      background: c.bg, border: `1px solid ${c.bd}`,
      fontSize: 12, fontWeight: 500, color: c.fg,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: c.dot, flexShrink: 0,
        ...(status === 'running' ? { animation: 'mcPulse 1.5s ease-in-out infinite' } : {}),
      }} />
      {c.label}
    </span>
  );
}

// derive display status helper (shared)
function deriveDisplayStatus(run) {
  let s = (run.status || '').toString();
  if (s === 'done') s = 'success';
  if (s === 'failed') s = 'error';
  const out = (run.output || '') + '';
  if (out.includes('❌ Error: All AI models failed:') || out.includes('All AI models failed:')) return 'error';
  if (run.error) return 'error';
  return s || 'success';
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, color }) {
  return (
    <div style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 12, padding: '16px 20px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', color: T.ink4, marginBottom: 10, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

// ── Step item icon ────────────────────────────────────────────────────────────
function StepIcon({ status }) {
  if (status === 'done' || status === 'success' || status === 'completed') {
    return (
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.greenBg, border: `1.5px solid ${T.greenBd}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <CheckCircle2 size={14} color={T.green} />
      </div>
    );
  }
  if (status === 'running' || status === 'in_progress') {
    return (
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.accentLight, border: `1.5px solid ${T.accentMid}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Loader2 size={13} color={T.accent} style={{ animation: 'mcSpin 1s linear infinite' }} />
      </div>
    );
  }
  if (status === 'error' || status === 'failed') {
    return (
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.redBg, border: `1.5px solid ${T.redBd}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <X size={13} color={T.red} />
      </div>
    );
  }
  // pending
  return (
    <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.surface2, border: `1.5px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Clock size={13} color={T.ink4} />
    </div>
  );
}

// ── Build steps from a run record ─────────────────────────────────────────────
function buildSteps(run) {
  // derive display status (normalize done/failed and detect AI-model failure messages)
  const deriveStatus = (r) => {
    let s = (r.status || '').toString();
    if (s === 'done') s = 'success';
    if (s === 'failed') s = 'error';
    // output-based detection for AI model failure
    const out = (r.output || '') + '';
    if (out.includes('❌ Error: All AI models failed:') || out.includes('All AI models failed:')) return 'error';
    if (r.error) return 'error';
    return s || 'success';
  };

  // Canvas workflow node results
  if (Array.isArray(run.nodeResults) && run.nodeResults.length > 0) {
    return run.nodeResults.map((n, i) => ({
      id: i,
      title: n.nodeName || n.name || `Step ${i + 1}`,
      desc:  n.output ? (friendlyRunError(n.output).slice(0, 80) + (friendlyRunError(n.output).length > 80 ? '…' : '')) : '',
      status: n.error ? 'error' : n.output ? 'done' : 'pending',
      duration: n.durationMs,
    }));
  }
  // Agent skill/tool results
  if (Array.isArray(run.skillResults) && run.skillResults.length > 0) {
    return run.skillResults.map((s, i) => ({
      id: i,
      title: s.name || s.skillId || s.tool || `Tool ${i + 1}`,
      desc:  s.summary || s.result?.slice?.(0, 80) || '',
      status: s.error ? 'error' : 'done',
      duration: s.durationMs,
    }));
  }
  // Canvas workflow runs persist their concise node history in `steps`.
  if (Array.isArray(run.steps) && run.steps.length > 0) {
    return run.steps.map((step, i) => ({
      id: i,
      title: step.name || `Step ${i + 1}`,
      desc: step.description || '',
      status: step.status === 'success' ? 'done' : (step.status || 'pending'),
      duration: null,
    }));
  }
  // Fallback — single-step derived from run itself
  const display = deriveStatus(run);
  return [{
    id: 0,
    title: run.agentName || (run.chainId ? 'Workflow' : 'Agent'),
    desc:  run.output ? friendlyRunError(run.output).slice(0, 80) + (friendlyRunError(run.output).length > 80 ? '…' : '') : (run.status === 'running' ? 'In progress…' : ''),
    status: (display === 'success' ? 'done' : display),
    duration: run.duration,
  }];
}

const TABS = ['Steps', 'Trace', 'Logs', 'Memory', 'Output', 'Evals'];

// ── Row pieces ────────────────────────────────────────────────────────────────
const fmtTokens = (n) => (n == null ? null : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
const SOURCE_STYLE = { Chat: { fg: 'var(--app-primary-deep)', bg: 'var(--app-primary-soft)' }, Scheduled: { fg: 'var(--lyra-color-warning)', bg: 'var(--lyra-color-warning-bg)' }, Workflow: { fg: 'var(--app-text-2)', bg: 'var(--app-surface-3)' }, Agent: { fg: 'var(--app-text-2)', bg: 'var(--app-surface-3)' } };

function ToolChips({ view, max = 3 }) {
  if (!view || !view.tools || view.tools.length === 0) return <span style={{ fontSize: 12, color: T.ink4 }}>No tools used</span>;
  const shown = view.tools.slice(0, max);
  const extra = view.toolTotal - shown.length;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
      {shown.map((t) => {
        const danger = t.failed || t.category === 'destructive';
        return (
          <span key={t.name} title={`${t.name}${t.count > 1 ? ` ×${t.count}` : ''}${t.failed ? ' (failed)' : ''}${t.category ? ` · ${t.category}` : ''}`}
            style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 99, color: danger ? T.red : T.ink2, background: danger ? T.redBg : T.surface3, border: `1px solid ${danger ? T.redBd : T.border}`, whiteSpace: 'nowrap' }}>
            {t.name}{t.count > 1 ? ` ×${t.count}` : ''}
          </span>
        );
      })}
      {extra > 0 && <span style={{ fontSize: 11, color: T.ink4 }}>+{extra}</span>}
    </div>
  );
}

function UsageLine({ view }) {
  if (!view) return null;
  const parts = [];
  if (view.llmCalls) parts.push(`${view.llmCalls} model call${view.llmCalls === 1 ? '' : 's'}`);
  if (view.tokens != null) parts.push(`${fmtTokens(view.tokens)} tokens`);
  return parts.length ? <div style={{ fontSize: 11.5, color: T.ink4, marginTop: 3 }}>{parts.join(' · ')}</div> : null;
}

function HealthBadge({ view, status }) {
  if (status === 'running') return <span style={{ fontSize: 12, color: T.ink4 }}>…</span>;
  const h = view?.health;
  if (!h) return <span style={{ fontSize: 12, color: T.ink4 }}>—</span>;
  const ok = h.passed === h.total;
  const bad = h.passed < h.total - 1;
  const s = ok ? { fg: T.green, bg: T.greenBg, bd: T.greenBd } : bad ? { fg: T.red, bg: T.redBg, bd: T.redBd } : { fg: T.amber, bg: T.amberBg, bd: T.amberBd };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span title={ok ? 'All automatic checks passed' : `Failed: ${h.failed.join(', ')}`} style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 8px', borderRadius: 99, color: s.fg, background: s.bg, border: `1px solid ${s.bd}` }}>
        {ok ? <Check size={11} style={{ verticalAlign: -1 }} /> : <AlertTriangle size={11} style={{ verticalAlign: -1 }} />} {h.passed}/{h.total}
      </span>
      {view.feedback === 'up' && <ThumbsUp size={12} color="var(--lyra-color-success)" />}
      {view.feedback === 'down' && <ThumbsDown size={12} color="var(--lyra-color-danger)" />}
    </span>
  );
}

function RunTitle({ run, isSelected }) {
  const v = run.view;
  const src = SOURCE_STYLE[v?.source] || SOURCE_STYLE.Agent;
  return (
    <div style={{ minWidth: 0 }}>
      <div title={run.input || ''} style={{ fontSize: 13.5, fontWeight: isSelected ? 700 : 600, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {v?.title || run.agentName || (run.chainId ? 'Workflow' : 'Agent')}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 11.5, color: T.ink4, minWidth: 0 }}>
        {v?.source && <span style={{ fontWeight: 700, padding: '0 6px', borderRadius: 99, color: src.fg, background: src.bg, flexShrink: 0 }}>{v.source}</span>}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.agentName || ''}{run.modelName ? ` · ${run.modelName}` : ''}</span>
      </div>
    </div>
  );
}

// ── Trace tab: every model call with the tool calls it asked for, and the conversation the run belongs to ──
const CATEGORY_STYLE = {
  read:        { fg: T.ink3,   bg: T.surface3 },
  write:       { fg: T.accentDark, bg: T.accentLight },
  destructive: { fg: T.red,    bg: T.redBg },
};
const ms = (n) => (n == null ? '—' : n < 1000 ? `${n} ms` : `${(n / 1000).toFixed(1)} s`);

function TraceTab({ run }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(() => new Set());
  useEffect(() => {
    setData(null); setError('');
    evalsAPI.runTrace(run._id).then(setData).catch(() => setError('The trace is not available for this run.'));
  }, [run._id]);
  if (error) return <div style={{ color: T.ink4, fontSize: 13, padding: '24px 0', textAlign: 'center' }}>{error}</div>;
  if (!data) return <div style={{ padding: '32px 0', textAlign: 'center' }}><Loader2 size={18} style={{ animation: 'mcSpin 1s linear infinite', color: T.accent }} /></div>;

  const { llmCalls, tools, session } = data;
  const tokensIn = llmCalls.reduce((a, c) => a + (c.promptTokens || 0), 0);
  const tokensOut = llmCalls.reduce((a, c) => a + (c.completionTokens || 0), 0);
  const cached = llmCalls.reduce((a, c) => a + (c.cachedTokens || 0), 0);
  const toolsFor = (callIndex) => tools.filter((t) => t.callIndex === callIndex);
  const orphan = tools.filter((t) => t.callIndex == null || !llmCalls.some((c) => c.index === t.callIndex));
  const toggle = (k) => setOpen((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11.5 };

  const Tool = ({ t }) => {
    const cat = CATEGORY_STYLE[t.category] || CATEGORY_STYLE.read;
    const k = `t${t.i}`;
    return (
      <div style={{ marginLeft: 22, padding: '7px 10px', borderLeft: `2px solid ${t.ok ? T.border : T.red}`, background: t.ok ? 'transparent' : T.redBg, borderRadius: '0 8px 8px 0', marginTop: 6 }}>
        <button onClick={() => toggle(k)} style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, width: '100%', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: t.ok ? T.ink : T.red }}>{String(t.name).split('__').pop()}</span>
          {t.category && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 99, color: cat.fg, background: cat.bg }}>{t.category}</span>}
          {t.parseStatus !== 'ok' && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 99, color: T.red, background: T.redBg }}>bad arguments</span>}
          <span style={{ fontSize: 11.5, color: T.ink4 }}>{ms(t.ms)}{t.resultTokens != null ? ` · ${t.resultTokens} result tokens` : ''}</span>
        </button>
        {t.error && <div style={{ fontSize: 12, color: T.red, marginTop: 3 }}>{friendlyRunError(t.error)}</div>}
        {open.has(k) && t.input && <pre style={{ ...mono, margin: '6px 0 0', color: T.ink3, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(t.input, null, 2)}</pre>}
      </div>
    );
  };

  return (
    <div>
      {session && (
        <a href={`/dashboard?session=${session.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 14, textDecoration: 'none', color: T.ink2, fontSize: 13 }}>
          <MessageSquare size={14} style={{ flexShrink: 0 }} /> <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Conversation: <b style={{ color: T.ink }}>{session.title}</b></span> <ChevronRight size={14} style={{ flexShrink: 0 }} />
        </a>
      )}
      {llmCalls.length > 0 ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
            {[['Model calls', llmCalls.length], ['Tokens in → out', `${tokensIn} → ${tokensOut}`], ['Cached', cached || '—']].map(([l, v]) => (
              <div key={l} style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: '8px 10px' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: T.ink4 }}>{l}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{v}</div>
              </div>
            ))}
          </div>
          {llmCalls.map((c) => (
            <div key={c.index} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ width: 22, height: 22, borderRadius: 7, background: T.accentLight, color: T.accentDark, fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{c.index}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{c.model}</span>
                <span style={{ fontSize: 12, color: T.ink3 }}>{ms(c.latencyMs)} · {c.promptTokens ?? '?'}→{c.completionTokens ?? '?'} tokens · {c.finishReason || '—'}</span>
              </div>
              <div style={{ fontSize: 11.5, color: T.ink4, margin: '3px 0 0 30px' }}>
                {c.trigger === 'user' ? 'Triggered by your message' : 'Triggered by tool results'}{c.toolsRequested?.length ? ` · asked for ${c.toolsRequested.map((n) => String(n).split('__').pop()).join(', ')}` : ' · wrote the answer'}
              </div>
              {toolsFor(c.index).map((t) => <Tool key={t.i} t={t} />)}
            </div>
          ))}
          {orphan.length > 0 && <>{orphan.map((t) => <Tool key={t.i} t={t} />)}</>}
        </>
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 10 }}>This run was recorded before model-call tracing, so only its tool calls are listed.</div>
          {tools.length === 0 && <div style={{ color: T.ink4, fontSize: 13 }}>No tool calls.</div>}
          {tools.map((t) => <Tool key={t.i} t={t} />)}
        </>
      )}
    </div>
  );
}

// ── Evals tab of the run drawer: automatic checks, feedback, AI judge, and "add to evals" ──
function RunEvalsTab({ run }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [judgeError, setJudgeError] = useState('');
  const [busy, setBusy] = useState('');
  // External evaluation is the default. This is intentionally a per-drawer
  // choice: it does not mutate the server-wide rollout environment variable.
  const [evalHarnessRequired, setEvalHarnessRequired] = useState(true);
  const load = useCallback(async () => {
    try {
      const next = await evalsAPI.runChecks(run._id);
      setData(next);
      setError('');
    } catch (_) {
      setError('Checks are not available for this run.');
    }
  }, [run._id]);
  useEffect(() => { setData(null); setError(''); load(); }, [load]);

  // The evaluator is delivered through an outbox worker, so it can complete
  // after the run drawer first opens. Keep this view current until the report
  // reaches a terminal delivery state; do not make the user change tabs.
  const deliveryState = data?.externalEvaluation?.state;
  const externalInFlight = evalHarnessRequired && ['pending', 'processing'].includes(deliveryState);
  useEffect(() => {
    if (!externalInFlight) return undefined;
    const timer = window.setInterval(load, 2500);
    return () => window.clearInterval(timer);
  }, [externalInFlight, load]);

  const rate = async (rating) => {
    const next = data?.feedback?.rating === rating ? null : rating;
    setBusy('rate');
    try { const d = await evalsAPI.feedback(run._id, next); setData((x) => ({ ...x, feedback: d.feedback })); }
    catch (_) { toast.error('Could not save feedback'); }
    finally { setBusy(''); }
  };
  const judge = async () => {
    setJudgeError('');
    setBusy('judge');
    try { const d = await evalsAPI.judge(run._id, undefined, evalHarnessRequired); setData((x) => ({ ...x, judge: d.judge })); }
    catch (e) {
      const message = e?.response?.data?.error || 'The judge could not score this run';
      setJudgeError(message);
      toast.error(message);
    }
    finally { setBusy(''); }
  };
  const refresh = async () => {
    setBusy('refresh');
    setError('');
    try { await load(); }
    catch (_) { /* load stores the visible error */ }
    finally { setBusy(''); }
  };
  const addCase = async () => {
    setBusy('case');
    try { await evalsAPI.runToCase(run._id); toast.success('Added to My evals. It will run with your next evaluation.'); }
    catch (e) { toast.error(e?.response?.data?.error || 'Could not add this run'); }
    finally { setBusy(''); }
  };
  const retryExternalEvaluation = async () => {
    setBusy('external-retry');
    try {
      await evalsAPI.retryExternalEvaluation(run._id);
      toast.success('External evaluation re-queued. The report will update shortly.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not re-run the external evaluation');
    } finally { setBusy(''); }
  };

  if (error) return <div style={{ color: T.ink4, fontSize: 13, padding: '24px 0', textAlign: 'center' }}>{error}</div>;
  if (!data) return <div style={{ padding: '32px 0', textAlign: 'center' }}><Loader2 size={18} style={{ animation: 'mcSpin 1s linear infinite', color: T.accent }} /></div>;
  const btn = (active, danger) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 12px', borderRadius: 9, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${active ? (danger ? T.redBd : T.greenBd) : T.border}`, background: active ? (danger ? T.redBg : T.greenBg) : T.surface, color: active ? (danger ? T.red : T.green) : T.ink2 });
  const label = (t) => <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: T.ink4, margin: '18px 0 8px' }}>{t}</div>;
  const external = data.externalEvaluation;
  const report = external?.report;
  const judgment = report?.judgment;
  const externalFailed = external?.state === 'dead' || judgment?.status === 'error';
  const externalPending = ['pending', 'processing'].includes(external?.state);
  const evidence = report?.evidence || {};
  const evidenceEntries = Object.entries(evidence).filter(([key]) => key !== 'coverage');
  const scoreLabel = (value) => value == null ? 'Not scored' : `${Math.round(value)}%`;
  const judgeReady = !!data.judge?.reason;
  const judgeFailed = !!judgeError;
  return (
    <div>
      <div style={{ border: `1px solid ${judgeFailed ? T.redBd : judgeReady ? (data.judge.pass ? T.greenBd : T.redBd) : T.border}`, background: judgeFailed ? T.redBg : judgeReady ? (data.judge.pass ? T.greenBg : T.redBg) : T.surface2, borderRadius: 12, padding: 14, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5, fontWeight: 700, color: T.ink }}>
              {judgeFailed ? <AlertTriangle size={16} color={T.red} /> : judgeReady && data.judge.pass ? <CheckCircle2 size={16} color={T.green} /> : <Sparkles size={16} color={judgeReady ? T.red : T.accent} />}
              Independent evaluation
            </div>
            <div style={{ fontSize: 12.5, color: judgeFailed ? T.red : T.ink3, lineHeight: 1.45, marginTop: 5 }}>
              {judgeFailed ? judgeError : judgeReady ? `${data.judge.pass ? 'Passed' : 'Needs review'} · ${Math.round((data.judge.score ?? 0) * 100)}% confidence` : 'Evaluate the outcome and tool trajectory with the independent judge.'}
            </div>
          </div>
          <button title="Refresh run data" disabled={busy === 'refresh'} onClick={refresh} style={{ ...btn(false), padding: 7, flexShrink: 0 }}>
            <RefreshCw size={14} style={busy === 'refresh' ? { animation: 'mcSpin 1s linear infinite' } : {}} />
          </button>
        </div>
        {judgeReady && <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 10 }}>{data.judge.reason}</div>}
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 12, fontSize: 12.5, color: T.ink2, cursor: 'pointer' }}>
          <input type="checkbox" checked={evalHarnessRequired} onChange={(e) => { setEvalHarnessRequired(e.target.checked); setJudgeError(''); }} style={{ accentColor: T.accent, width: 15, height: 15, margin: 0 }} />
          <span><b>EVAL_HARNESS_REQUIRED</b> · {evalHarnessRequired ? 'Use external evaluator (required)' : 'Use Lyra internal judge'}</span>
        </label>
        <div style={{ fontSize: 11.5, color: T.ink4, lineHeight: 1.45, marginTop: 5, marginLeft: 24 }}>
          {evalHarnessRequired ? 'A provider failure is shown as an evaluation error; Lyra will not substitute an internal result.' : 'Uses Lyra’s configured judge instead of the external evaluator.'}
        </div>
        <button disabled={busy === 'judge'} onClick={judge} style={{ ...btn(false), width: '100%', marginTop: 12, borderColor: T.accent, background: T.accent, color: '#fff' }}>
          {busy === 'judge' ? <Loader2 size={14} style={{ animation: 'mcSpin 1s linear infinite' }} /> : <RefreshCw size={14} />}
          {busy === 'judge' ? 'Evaluating…' : judgeReady || judgeFailed ? 'Run evaluation again' : evalHarnessRequired ? 'Run external evaluation' : 'Run internal evaluation'}
        </button>
      </div>

      {label(`Automatic checks · ${data.passed}/${data.total} passed`)}
      {data.checks.map((c) => (
        <div key={c.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
          <span style={{ color: c.pass ? T.green : T.red, marginTop: 1 }}>{c.pass ? <Check size={15} /> : <X size={15} />}</span>
          <div><div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{c.label}</div><div style={{ fontSize: 12.5, color: c.pass ? T.ink3 : T.red, wordBreak: 'break-word' }}>{c.detail}</div></div>
        </div>
      ))}
      {data.tokens != null && <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 8 }}>{data.tokens} tokens used</div>}

      {label('Evaluation mode')}
      <div style={{ padding: 12, border: `1px solid ${T.border}`, borderRadius: 10, background: T.surface2 }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer' }}>
          <input type="checkbox" checked={evalHarnessRequired} onChange={(e) => setEvalHarnessRequired(e.target.checked)} style={{ width: 16, height: 16, marginTop: 1, accentColor: T.accent }} />
          <span>
            <span style={{ display: 'block', fontSize: 13, color: T.ink, fontWeight: 650 }}>EVAL_HARNESS_REQUIRED</span>
            <span style={{ display: 'block', fontSize: 12, color: T.ink3, lineHeight: 1.45, marginTop: 3 }}>
              {evalHarnessRequired ? 'External evaluation is required (default).' : 'Use Lyra’s internal AI judge instead.'}
            </span>
          </span>
        </label>
      </div>

      {evalHarnessRequired ? <>
        {label('Independent evaluation')}
        <div style={{ padding: 12, border: `1px solid ${externalFailed ? T.redBd : T.border}`, background: externalFailed ? T.redBg : T.surface, borderRadius: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: externalFailed ? T.red : T.ink }}>
              {externalFailed ? <AlertTriangle size={15} /> : externalPending ? <Loader2 size={15} color={T.accent} style={{ animation: 'mcSpin 1s linear infinite' }} /> : <CheckCircle2 size={15} color={T.green} />}
              {externalPending ? 'Evaluation in progress' : external ? `Delivery: ${external.state}` : 'Awaiting external evaluation'}
            </div>
            <button disabled={busy === 'external-retry' || externalPending} onClick={retryExternalEvaluation} style={{ ...btn(false), padding: '6px 9px', opacity: externalPending ? 0.55 : 1, cursor: externalPending ? 'wait' : 'pointer' }} title={externalPending ? 'Evaluation is already in progress' : 'Re-run external evaluation'} aria-label="Re-run external evaluation">
              <RefreshCw size={14} style={busy === 'external-retry' ? { animation: 'mcSpin 1s linear infinite' } : {}} />
              <span style={{ fontSize: 12 }}>Re-run</span>
            </button>
          </div>
          {externalPending && <div role="progressbar" aria-label={external.state === 'pending' ? 'External evaluation queued' : 'External evaluation in progress'} aria-valuetext="Loading evaluation report" style={{ height: 6, overflow: 'hidden', borderRadius: 99, marginTop: 14, background: T.accentLight, boxShadow: `inset 0 1px 1px ${T.accentMid}` }}>
            <div style={{ width: '42%', height: '100%', borderRadius: 'inherit', background: `linear-gradient(90deg, transparent 0%, ${T.accent} 32%, ${T.accentDark} 50%, ${T.accent} 68%, transparent 100%)`, animation: 'mcEvalProgress 1.45s cubic-bezier(.4,0,.2,1) infinite', boxShadow: `0 0 12px ${T.accentMid}` }} />
          </div>}
          {external?.evaluationId && <div style={{ fontSize: 11.5, color: T.ink4, marginTop: 5 }}>Evaluation {external.evaluationId} · attempt {external.attempts || 1}</div>}
          {report?.checks?.length > 0 && <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.ink4, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 5 }}>Validation checks</div>
            {report.checks.map((c, i) => {
              const pass = c.status === 'pass';
              const unavailable = c.status === 'unavailable';
              return <div key={`${c.name}-${i}`} style={{ display: 'flex', gap: 7, padding: '5px 0', fontSize: 12.5, color: pass ? T.ink2 : unavailable ? T.ink3 : T.red }}>
                {pass ? <Check size={14} color={T.green} /> : <X size={14} color={unavailable ? T.ink4 : T.red} />}<span><b>{c.name.replaceAll('_', ' ')}</b> · {c.status}{c.observed != null ? ` · observed: ${c.observed}` : ''}{c.failures != null ? ` · failures: ${c.failures}` : ''}</span>
              </div>;
            })}
          </div>}
          {judgment && <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.ink4, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 5 }}>Model judge</div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: judgment.status === 'error' ? T.red : T.ink }}>Status: {judgment.status}</div>
            {judgment.reason && <div style={{ fontSize: 12.5, color: judgment.status === 'error' ? T.red : T.ink2, marginTop: 3, wordBreak: 'break-word' }}>{judgment.reason}</div>}
            {judgment.model && <div style={{ fontSize: 12, color: T.ink3, marginTop: 4 }}>Model: {judgment.model}</div>}
            {judgment.providerStatus && <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>Provider HTTP status: {judgment.providerStatus}</div>}
            {judgment.providerErrorCode && <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>Provider diagnosis: {judgment.providerErrorCode.replaceAll('_', ' ')}</div>}
            {judgment.retryAfterSeconds != null && <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>Provider retry-after: {judgment.retryAfterSeconds}s</div>}
            {judgment.status === 'error' && judgment.providerStatus === 429 && <div style={{ fontSize: 12, color: T.red, marginTop: 5 }}>The evaluator reached OpenAI, but the provider rejected the request. Check the evaluator service’s OpenAI project billing, quota, and model access.</div>}
          </div>}
          {(report?.evidence_coverage != null || evidenceEntries.length > 0) && <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.ink4, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 5 }}>Evidence</div>
            {report?.evidence_coverage != null && <div style={{ fontSize: 12, color: T.ink3 }}>Coverage: {Math.round(report.evidence_coverage * 100)}%</div>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>{evidenceEntries.map(([name, available]) => <span key={name} style={{ padding: '3px 6px', borderRadius: 6, background: available ? T.greenBg : T.surface2, color: available ? T.green : T.ink4, fontSize: 11.5 }}>{available ? '✓' : '—'} {name.replace(/([A-Z])/g, ' $1').trim()}</span>)}</div>
          </div>}
          {report?.scores && <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.ink4, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 5 }}>Scores</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6, fontSize: 11.5 }}>
              <div><div style={{ color: T.ink4 }}>Execution</div><b>{scoreLabel(report.scores.execution_reliability)}</b></div><div><div style={{ color: T.ink4 }}>Response</div><b>{scoreLabel(report.scores.response_quality)}</b></div><div><div style={{ color: T.ink4 }}>Overall</div><b>{scoreLabel(report.scores.overall)}</b></div>
            </div>
          </div>}
          {report?.limitations?.length > 0 && <div style={{ fontSize: 12, color: T.red, marginTop: 10 }}>Limitations: {report.limitations.join('; ')}</div>}
          {external?.lastError && <div style={{ fontSize: 12, color: T.red, marginTop: 5 }}>Delivery error: {external.lastError}</div>}
          {!external && <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: T.ink3, marginTop: 8 }}><Loader2 size={12} style={{ animation: 'mcSpin 1s linear infinite' }} />Waiting for the external-evaluation job to be created.</div>}
        </div>
      </> : <>
        {label('Internal AI judge')}
        {data.judge?.reason ? (
          <div style={{ border: `1px solid ${data.judge.pass ? T.greenBd : T.redBd}`, background: data.judge.pass ? T.greenBg : T.redBg, borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: data.judge.pass ? T.green : T.red }}>{data.judge.pass ? 'Pass' : 'Fail'} · {Math.round((data.judge.score ?? 0) * 100)}%</div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4 }}>{data.judge.reason}</div>
          </div>
        ) : <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 8 }}>Scores the reply against Lyra’s configured model. Uses tokens, so it runs only when you ask.</div>}
        <button disabled={busy === 'judge'} onClick={judge} style={{ ...btn(false), marginTop: 8 }}>{busy === 'judge' ? <Loader2 size={14} style={{ animation: 'mcSpin 1s linear infinite' }} /> : <Sparkles size={14} />} {data.judge?.reason ? 'Score again' : 'Run internal evaluation'}</button>
      </>}
      {label('Was this run good?')}
      <div style={{ display: 'flex', gap: 8 }}>
        <button disabled={busy === 'rate'} onClick={() => rate('up')} style={btn(data.feedback?.rating === 'up')}><ThumbsUp size={14} /> Good</button>
        <button disabled={busy === 'rate'} onClick={() => rate('down')} style={btn(data.feedback?.rating === 'down', true)}><ThumbsDown size={14} /> Bad</button>
      </div>

      {label('Keep it from happening again')}
      <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 8 }}>Turns this prompt into a test case in My evals, so every future evaluation checks it.</div>
      <button disabled={busy === 'case'} onClick={addCase} style={btn(false)}><FlaskConical size={14} /> Add to evals</button>
    </div>
  );
}

// ── Run detail drawer ─────────────────────────────────────────────────────────
function RunDrawer({ run, stopping, onStop, onClose }) {
  const [tab, setTab] = useState('Steps');
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 900 : false));
  const steps = buildSteps(run);
  const isRunning = run.status === 'running';
  const elapsed = isRunning
    ? Date.now() - new Date(run.createdAt).getTime()
    : run.duration;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 50,
        width: isMobile ? '100vw' : 440, background: T.surface,
        borderLeft: `1px solid ${T.border}`,
        boxShadow: '-12px 0 48px rgba(0,0,0,0.08)',
        display: 'flex', flexDirection: 'column',
        animation: 'mcSlideIn .2s cubic-bezier(.4,0,.2,1)',
      }}>

        {/* ── Header ── */}
        <div style={{ padding: '20px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Avatar */}
              <div style={{
                width: 44, height: 44, borderRadius: 14, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: run.chainId ? 'var(--app-primary-soft)' : T.accentLight,
                border: `1.5px solid ${run.chainId ? '#C7D2FE' : T.accentMid}`,
              }}>
                {run.chainId
                  ? <GitBranch size={18} color="var(--lyra-color-primary)" />
                  : <Bot size={18} color={T.accent} />}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
                  {run.agentName || (run.chainId ? 'Workflow' : 'Agent')}
                </div>
                {/* Sub-line: dot + status + model + duration */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                    background: STATUS_CFG[run.status]?.dot || T.ink4,
                    ...(isRunning ? { animation: 'mcPulse 1.5s ease-in-out infinite' } : {}),
                  }} />
                  <span style={{ fontSize: 12.5, color: T.ink3 }}>
                    {STATUS_CFG[run.status]?.label || run.status}
                    {run.modelName ? ` · ${run.modelName}` : ''}
                    {elapsed ? ` · ${formatDuration(elapsed)}` : ''}
                  </span>
                </div>
              </div>
            </div>
            {/* Close + menu */}
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={onClose}
                style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.ink4 }}
                onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, gap: 0, overflowX: 'auto' }}>
            {TABS.map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: '8px 12px', fontSize: 13.5, fontWeight: tab === t ? 600 : 400, flexShrink: 0,
                  color: tab === t ? T.accent : T.ink3,
                  background: 'none', border: 'none',
                  borderBottom: `2px solid ${tab === t ? T.accent : 'transparent'}`,
                  cursor: 'pointer', fontFamily: 'inherit',
                  marginBottom: -1, transition: 'all .13s',
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab body (scrollable) ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* STEPS */}
          {tab === 'Steps' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {steps.map((step, idx) => (
                <div key={step.id} style={{ display: 'flex', gap: 14, paddingBottom: idx < steps.length - 1 ? 20 : 0, position: 'relative' }}>
                  {/* Vertical connector line */}
                  {idx < steps.length - 1 && (
                    <div style={{
                      position: 'absolute', left: 13, top: 30, bottom: 0,
                      width: 1.5, background: T.border,
                    }} />
                  )}
                  <StepIcon status={step.status} />
                  <div style={{ flex: 1, paddingTop: 4 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, marginBottom: 3 }}>{step.title}</div>
                    {step.desc && (
                      <div style={{ fontSize: 12.5, color: T.ink3, lineHeight: 1.5, marginBottom: 4 }}>{step.desc}</div>
                    )}
                    <div style={{ fontSize: 11.5, color: step.status === 'running' || step.status === 'in_progress' ? T.accent : T.ink4 }}>
                      {step.duration ? `${formatDuration(step.duration)} · ` : ''}
                      <span style={{ color: step.status === 'error' ? T.red : step.status === 'done' || step.status === 'completed' || step.status === 'success' ? T.green : step.status === 'running' || step.status === 'in_progress' ? T.accent : T.ink4 }}>
                        {step.status === 'done' || step.status === 'success' || step.status === 'completed' ? 'completed'
                          : step.status === 'running' || step.status === 'in_progress' ? 'in progress'
                          : step.status === 'error' ? 'failed'
                          : 'pending'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {steps.length === 0 && (
                <div style={{ textAlign: 'center', padding: '32px 0', color: T.ink4, fontSize: 13 }}>No steps recorded yet</div>
              )}
            </div>
          )}

          {/* LOGS */}
          {tab === 'Logs' && (
            <div style={{
              background: 'var(--lyra-color-text)', borderRadius: 10, padding: '14px 16px',
              fontFamily: 'ui-monospace, monospace', fontSize: 12, lineHeight: 1.7,
              color: 'var(--app-primary-soft-2)', minHeight: 200,
            }}>
              {run.logs && run.logs.length > 0
                ? run.logs.map((l, i) => <div key={i}>{l}</div>)
                : (
                  <div style={{ color: 'var(--lyra-color-text-muted)' }}>
                    {isRunning ? '> Agent running… logs will appear here' : '> No logs recorded for this run'}
                  </div>
                )
              }
              {isRunning && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: T.accentMid, marginTop: 4 }}>
                  <Loader2 size={11} style={{ animation: 'mcSpin 1s linear infinite' }} />
                  <span>Live…</span>
                </div>
              )}
            </div>
          )}

          {/* MEMORY */}
          {tab === 'Memory' && (
            <div>
              {Array.isArray(run.memories) && run.memories.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {run.memories.map((m, i) => (
                    <div key={i} style={{ padding: '10px 14px', background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10 }}>
                      <div style={{ fontSize: 12, color: T.ink4, marginBottom: 3 }}>Score: {m.score?.toFixed(2) ?? '—'}</div>
                      <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>{m.text || m.content}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px 0', color: T.ink4 }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>🧠</div>
                  <div style={{ fontSize: 13 }}>No memory context retrieved for this run</div>
                </div>
              )}
            </div>
          )}

          {/* OUTPUT */}
          {tab === 'Trace' && <TraceTab run={run} />}
          {tab === 'Evals' && <RunEvalsTab run={run} />}

          {tab === 'Output' && (
            <div>
              {isRunning ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 14px', background: T.accentLight, border: `1px solid ${T.accentMid}`, borderRadius: 10, color: T.accent, fontSize: 13 }}>
                  <Loader2 size={15} style={{ animation: 'mcSpin 1s linear infinite', flexShrink: 0 }} />
                  Running… output will appear when complete
                </div>
              ) : (
                <div style={{
                  background: run.status === 'error' ? T.redBg : T.surface2,
                  border: `1px solid ${run.status === 'error' ? T.redBd : T.border}`,
                  borderRadius: 10, padding: '14px 16px',
                  fontSize: 13, color: run.status === 'error' ? T.red : T.ink2,
                  lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  minHeight: 80,
                }}>
                  {friendlyRunError(run.output || run.error) || <span style={{ color: T.ink4, fontStyle: 'italic' }}>No output recorded</span>}
                </div>
              )}
              {run.input && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', color: T.ink4, marginBottom: 8, textTransform: 'uppercase' }}>Input</div>
                  <div style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px', fontSize: 13, color: T.ink3, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {run.input}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: '14px 20px', borderTop: `1px solid ${T.border}`,
          display: 'flex', gap: 10, flexShrink: 0,
        }}>
          <button
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              padding: '11px 0', borderRadius: 10,
              border: `1px solid ${T.border}`, background: T.surface2,
              color: T.ink2, fontSize: 13.5, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all .13s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = T.surface3; e.currentTarget.style.borderColor = T.border2; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.surface2; e.currentTarget.style.borderColor = T.border; }}
          >
            <MessageSquare size={14} />
            Ask about this run ↗
          </button>
          {isRunning && (
            <button
              onClick={() => onStop(run)}
              disabled={stopping.has(run._id)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                padding: '11px 0', borderRadius: 10,
                border: `1px solid ${T.border}`, background: T.surface2,
                color: T.ink2, fontSize: 13.5, fontWeight: 500,
                cursor: stopping.has(run._id) ? 'wait' : 'pointer',
                fontFamily: 'inherit', transition: 'all .13s',
                opacity: stopping.has(run._id) ? 0.6 : 1,
              }}
              onMouseEnter={e => { if (!stopping.has(run._id)) { e.currentTarget.style.background = T.redBg; e.currentTarget.style.borderColor = T.redBd; e.currentTarget.style.color = T.red; } }}
              onMouseLeave={e => { e.currentTarget.style.background = T.surface2; e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.ink2; }}
            >
              {stopping.has(run._id)
                ? <Loader2 size={14} style={{ animation: 'mcSpin 1s linear infinite' }} />
                : <Square size={13} />}
              Stop
            </button>
          )}
        </div>
      </div>
    </>
  );
}

const ROW_COLS = 'minmax(0,1.5fr) minmax(0,1.1fr) 92px 116px 84px 84px 120px';

// ── Filter tab ────────────────────────────────────────────────────────────────
const FILTERS = ['All', 'Running', 'Agents', 'Workflows', 'Failed', 'Needs attention'];

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MissionControlPage() {
  const [runs,        setRuns]        = useState([]);
  const [stats,       setStats]       = useState({ runningNow: 0, completedToday: 0, failedToday: 0, avgDurationMs: 0 });
  const [loading,     setLoading]     = useState(true);
  // Deep links from Home ("7 runs failed today") arrive as ?filter=failed&since=today — apply them once, then drop them from the URL.
  const [filter,      setFilter]      = useState(() => {
    const want = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('filter')?.toLowerCase();
    return FILTERS.find((f) => f.toLowerCase() === want) || 'All';
  });
  const [sinceToday,  setSinceToday]  = useState(() => new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('since') === 'today');
  const [search,      setSearch]      = useState('');
  const [isMobile,    setIsMobile]    = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 900 : false));
  const [stopping,    setStopping]    = useState(new Set());
  const [rerunning,   setRerunning]   = useState(new Set());
  const [stoppingAll, setStoppingAll] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [groupByChat, setGroupByChat] = useState(() => { try { return localStorage.getItem('lyra:mc:groupByChat') === '1'; } catch { return false; } });
  const [collapsed,   setCollapsed]   = useState(() => new Set());
  useEffect(() => { try { localStorage.setItem('lyra:mc:groupByChat', groupByChat ? '1' : '0'); } catch { /* not remembered */ } }, [groupByChat]);
  const timerRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('tab') === 'evals' ? 'Evals' : 'Runs';
  const setView = (v) => setSearchParams(v === 'Evals' ? { tab: 'evals' } : {}, { replace: true });
  useEffect(() => {
    if (searchParams.has('filter') || searchParams.has('since')) {
      const next = new URLSearchParams(searchParams); next.delete('filter'); next.delete('since');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchRuns = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await agentRunAPI.listAll({ limit: 100 });
      setRuns(data.runs   || []);
      setStats(data.stats || { runningNow: 0, completedToday: 0, failedToday: 0, avgDurationMs: 0 });
      // Keep selectedRun in sync
      if (data.runs) {
        setSelectedRun(prev => prev ? (data.runs.find(r => r._id === prev._id) || prev) : null);
      }
    } catch (e) {
      console.error('[MissionControl]', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Adaptive polling
  const scheduleNext = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const hasActive = runs.some(r => r.status === 'running');
    timerRef.current = setTimeout(() => fetchRuns(true).then(scheduleNext), hasActive ? 3000 : 15000);
  }, [runs, fetchRuns]);

  useEffect(() => {
    fetchRuns().then(scheduleNext);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []); // eslint-disable-line

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const prevHasActive = useRef(false);
  useEffect(() => {
    const hasActive = runs.some(r => r.status === 'running');
    if (hasActive !== prevHasActive.current) { prevHasActive.current = hasActive; scheduleNext(); }
  }, [runs, scheduleNext]);

  // ── Stop ──────────────────────────────────────────────────────────────────
  const handleStop = async (run) => {
    setStopping(s => new Set(s).add(run._id));
    try {
      await agentRunAPI.stop(run._id);
      const update = r => r._id === run._id ? { ...r, status: 'stopped' } : r;
      setRuns(prev => prev.map(update));
      setSelectedRun(prev => prev?._id === run._id ? { ...prev, status: 'stopped' } : prev);
      setStats(s => ({ ...s, runningNow: Math.max(0, s.runningNow - 1) }));
      try { window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: { runId: run._id } })); } catch (_) {}
    } finally {
      setStopping(s => { const n = new Set(s); n.delete(run._id); return n; });
    }
  };

  const handleStopAll = async () => {
    const runningRuns = runs.filter((r) => r.status === 'running');
    if (!runningRuns.length) return;
    setStoppingAll(true);
    try {
      await Promise.allSettled(runningRuns.map((r) => agentRunAPI.stop(r._id)));
      setRuns((prev) => prev.map((r) => (r.status === 'running' ? { ...r, status: 'stopped' } : r)));
      setSelectedRun((prev) => (prev?.status === 'running' ? { ...prev, status: 'stopped' } : prev));
      setStats((s) => ({ ...s, runningNow: 0 }));
      try { window.dispatchEvent(new CustomEvent('lyra:run-end')); } catch (_) {}
    } finally {
      setStoppingAll(false);
      fetchRuns(true);
    }
  };

  const handleReRun = async (run) => {
    if (run.chainId) return;
    const agentId = run.agentId;
    if (!agentId) return;
    setRerunning((s) => new Set(s).add(run._id));
    try {
      const payload = {
        agentId,
        agentName: run.agentName || null,
        input: run.input || '',
      };
      const created = await agentRunAPI.createRun(payload);
      setRuns((prev) => [{
        ...(created?.run || {}),
        status: 'running',
        createdAt: (created?.run?.createdAt || new Date().toISOString()),
      }, ...prev]);
      setStats((s) => ({ ...s, runningNow: (s.runningNow || 0) + 1 }));
      try { window.dispatchEvent(new CustomEvent('lyra:run-start', { detail: { runId: created?.runId } })); } catch (_) {}

      // Trigger the actual run through existing global drawer pipeline with same input.
      try {
        const mention = run.agentName ? `@${run.agentName} ` : '';
        window.dispatchEvent(new CustomEvent('lyra:open-run-drawer', {
          detail: { agentId, prepopulateText: `${mention}${run.input || ''}`.trim() },
        }));
      } catch (_) {}
    } finally {
      setRerunning((s) => { const n = new Set(s); n.delete(run._id); return n; });
    }
  };

  const openRunDrawerFor = async (run) => {
    try {
      const res = await agentRunAPI.getRun(run._id);
      const fullRun = res?.run || run;
      setSelectedRun(fullRun);
    } catch (_) {
      setSelectedRun(run);
    }
  };

  // ── Filter ────────────────────────────────────────────────────────────────
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const filtered = runs.filter(r => {
    const q = search.trim().toLowerCase();
    const isW = !!r.chainId;
    if (q && !((r.agentName || '').toLowerCase().includes(q) || (r.view?.title || r.input || '').toLowerCase().includes(q))) return false;
    if (sinceToday && !(new Date(r.createdAt) >= startOfToday)) return false;
    if (filter === 'Needs attention') return !!r.view?.needsAttention;
    if (filter === 'Running')   return r.status === 'running';
    if (filter === 'Agents')    return !isW;
    if (filter === 'Workflows') return isW;
    if (filter === 'Failed')    return r.status === 'error';
    return true;
  });

  const attentionCount = runs.filter((r) => r.view?.needsAttention).length;

  // Optional grouping: runs that belong to the same saved chat sit under one header.
  const entries = (() => {
    if (!groupByChat) return filtered.map((run) => ({ kind: 'run', run }));
    const order = []; const groups = new Map();
    filtered.forEach((run) => {
      const key = run.view?.sessionId || `solo:${run._id}`;
      if (!groups.has(key)) { groups.set(key, []); order.push(key); }
      groups.get(key).push(run);
    });
    const out = [];
    order.forEach((key) => {
      const list = groups.get(key);
      if (key.startsWith('solo:')) { list.forEach((run) => out.push({ kind: 'run', run })); return; }
      out.push({ kind: 'group', key, list });
      if (!collapsed.has(key)) list.forEach((run) => out.push({ kind: 'run', run, indent: true }));
    });
    return out;
  })();

  // Stop / Re-run as before; chat runs get "Open chat" instead of an empty cell.
  const renderActions = (run, compact) => {
    const isRunning = run.status === 'running';
    const isChat = run.agentId === 'lyra-chat';
    const failed = run.status === 'stopped' || run.status === 'error' || run.status === 'failed';
    const pad = compact ? '7px 10px' : '5px 8px';
    const fs = compact ? 12 : 11.5;
    const base = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: pad, borderRadius: compact ? 8 : 7, fontSize: fs, fontWeight: 600, textDecoration: 'none' };
    if (isRunning) {
      return (
        <button onClick={(e) => { e.stopPropagation(); handleStop(run); }} disabled={stopping.has(run._id)}
          style={{ ...base, border: `1px solid ${T.redBd}`, background: T.redBg, color: T.red, cursor: stopping.has(run._id) ? 'wait' : 'pointer', opacity: stopping.has(run._id) ? 0.7 : 1 }}>
          {stopping.has(run._id) ? <Loader2 size={11} style={{ animation: 'mcSpin 1s linear infinite' }} /> : <Square size={11} />} Stop
        </button>
      );
    }
    if (!run.chainId && !isChat && (failed || run.view?.needsAttention)) {
      return (
        <button onClick={(e) => { e.stopPropagation(); handleReRun(run); }} disabled={rerunning.has(run._id)}
          style={{ ...base, border: `1px solid ${T.greenBd}`, background: T.greenBg, color: T.green, cursor: rerunning.has(run._id) ? 'wait' : 'pointer', opacity: rerunning.has(run._id) ? 0.7 : 1 }}>
          {rerunning.has(run._id) ? <Loader2 size={11} style={{ animation: 'mcSpin 1s linear infinite' }} /> : <RefreshCw size={11} />} Re-run
        </button>
      );
    }
    if (run.view?.sessionId) {
      return (
        <a href={`/dashboard?session=${run.view.sessionId}`} onClick={(e) => e.stopPropagation()} title={run.view.sessionTitle || 'Open the conversation'}
          style={{ ...base, border: `1px solid ${T.border}`, background: T.surface, color: T.ink2 }}>
          <MessageSquare size={11} /> Open chat
        </a>
      );
    }
    return <span style={{ fontSize: 11.5, color: T.ink4 }}>—</span>;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: T.page, fontFamily: 'DM Sans, system-ui, sans-serif', boxSizing: 'border-box' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
        @keyframes mcPulse  { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes mcSpin   { to{transform:rotate(360deg)} }
        @keyframes mcSlideIn { from{transform:translateX(100%)} to{transform:translateX(0)} }
        @keyframes mcEvalProgress { from{transform:translateX(-145%)} to{transform:translateX(340%)} }
        @media (prefers-reduced-motion: reduce) { * { animation-duration: .01ms !important; animation-iteration-count: 1 !important; } }
      `}</style>

      <div style={{ padding: isMobile ? '14px 12px 40px' : '28px 32px 60px', maxWidth: selectedRun && !isMobile ? 'calc(100vw - 460px)' : '100%', transition: 'max-width .2s' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18, gap: 10, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: T.ink, margin: 0, letterSpacing: '-0.3px' }}>Mission Control</h1>
            <p style={{ fontSize: 13.5, color: T.ink3, margin: '4px 0 0' }}>Live and recent agent + workflow runs</p>
          </div>
          <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' }}>
            <button
              onClick={() => fetchRuns()}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '8px 18px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, color: T.ink2, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .13s', width: isMobile ? 'calc(50% - 4px)' : 'auto' }}
              onMouseEnter={e => { e.currentTarget.style.background = T.surface2; e.currentTarget.style.borderColor = T.border2; }}
              onMouseLeave={e => { e.currentTarget.style.background = T.surface; e.currentTarget.style.borderColor = T.border; }}
            >
              <RefreshCw size={13} style={loading ? { animation: 'mcSpin 1s linear infinite' } : {}} />
              Refresh
            </button>
            <button
              onClick={handleStopAll}
              disabled={stoppingAll || !runs.some((r) => r.status === 'running')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '8px 18px', borderRadius: 10, border: `1px solid ${T.redBd}`, background: T.redBg, color: T.red, fontSize: 13, fontWeight: 600, cursor: stoppingAll || !runs.some((r) => r.status === 'running') ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all .13s', width: isMobile ? 'calc(50% - 4px)' : 'auto', opacity: stoppingAll || !runs.some((r) => r.status === 'running') ? 0.6 : 1 }}
            >
              {stoppingAll ? <Loader2 size={13} style={{ animation: 'mcSpin 1s linear infinite' }} /> : <Square size={12} />}
              Stop all
            </button>
          </div>
        </div>

        {/* ── View tabs ── */}
        <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
          {[['Runs', 'Runs'], ['Evals', 'Evals']].map(([v, label]) => (
            <button key={v} onClick={() => setView(v)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', fontSize: 14, fontWeight: view === v ? 600 : 400, color: view === v ? T.accent : T.ink3, background: 'none', border: 'none', borderBottom: `2px solid ${view === v ? T.accent : 'transparent'}`, marginBottom: -1, cursor: 'pointer', fontFamily: 'inherit' }}>
              {v === 'Evals' && <FlaskConical size={14} />}{label}
            </button>
          ))}
        </div>

        {view === 'Evals' ? <EvalsPanel isMobile={isMobile} /> : (<>

        {/* ── Stats ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
          <StatCard label="Running Now"     value={stats.runningNow}                      color={T.green} />
          <StatCard label="Completed Today" value={stats.completedToday}                  color={T.ink}   />
          <StatCard label="Failed Today"    value={stats.failedToday}                     color={T.red}   />
          <StatCard label="Avg Duration"    value={formatAvg(stats.avgDurationMs) || '—'} color={T.ink}   />
        </div>

        {/* ── Search + Filters ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.ink4, pointerEvents: 'none' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search runs…"
              style={{ paddingLeft: 30, paddingRight: 12, height: 36, width: isMobile ? '100%' : 200, minWidth: isMobile ? '100%' : 'auto', borderRadius: 9, border: `1px solid ${T.border}`, background: T.surface, color: T.ink, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color .13s' }}
              onFocus={e => e.target.style.borderColor = T.accentMid}
              onBlur={e  => e.target.style.borderColor = T.border}
            />
          </div>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', width: isMobile ? '100%' : 'auto', paddingBottom: isMobile ? 2 : 0 }}>
            {FILTERS.map(f => (
              <button
                key={f} onClick={() => setFilter(f)}
                style={{ padding: '7px 16px', borderRadius: 9, border: `1px solid ${filter === f ? T.border2 : T.border}`, background: filter === f ? T.surface3 : T.surface, color: filter === f ? T.ink : T.ink3, fontSize: 13, fontWeight: filter === f ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .13s' }}
                onMouseEnter={e => { if (filter !== f) e.currentTarget.style.background = T.surface2; }}
                onMouseLeave={e => { if (filter !== f) e.currentTarget.style.background = T.surface; }}
              >
                {f}{f === 'Needs attention' && attentionCount > 0 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '0 6px', borderRadius: 99, color: T.red, background: T.redBg }}>{attentionCount}</span>}
              </button>
            ))}
            {sinceToday && (
              <button onClick={() => setSinceToday(false)} title="Show runs from any day"
                style={{ padding: '7px 12px', borderRadius: 9, border: `1px solid ${T.border2}`, background: T.surface3, color: T.ink, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Today ✕
              </button>
            )}
          </div>
          <label style={{ marginLeft: isMobile ? 0 : 'auto', display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: T.ink2, cursor: 'pointer' }} title="Put runs from the same chat under one heading">
            <input type="checkbox" checked={groupByChat} onChange={(e) => setGroupByChat(e.target.checked)} /> Group by chat
          </label>
        </div>

        {/* ── Table ── */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>

          {/* Header */}
          {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: ROW_COLS, gap: 12, padding: '10px 20px', borderBottom: `1px solid ${T.border}`, background: T.surface2 }}>
              {['RUN', 'ACTIVITY', 'HEALTH', 'STATUS', 'STARTED', 'DURATION', 'ACTIONS'].map(h => (
                <div key={h} style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', color: T.ink4 }}>{h}</div>
              ))}
            </div>
          )}

          {/* Loading */}
          {loading && runs.length === 0 && (
            <div style={{ padding: '48px 20px', textAlign: 'center', color: T.ink4 }}>
              <Loader2 size={22} style={{ animation: 'mcSpin 1s linear infinite', margin: '0 auto 10px', display: 'block', color: T.accent }} />
              <div style={{ fontSize: 13 }}>Loading runs…</div>
            </div>
          )}

          {/* Empty */}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: '56px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.ink2, marginBottom: 6 }}>{filter === 'Needs attention' ? 'Nothing needs attention' : 'No runs found'}</div>
              <div style={{ fontSize: 13, color: T.ink4 }}>
                {filter === 'Needs attention' ? 'Runs with failed checks, tool errors or a thumbs-down show up here' : search || filter !== 'All' ? 'Try a different filter or search term' : 'Runs appear here when agents or workflows execute'}
              </div>
            </div>
          )}

          {/* Rows */}
          {entries.map((entry, idx) => {
            const last = idx === entries.length - 1;
            if (entry.kind === 'group') {
              const open = !collapsed.has(entry.key);
              const first = entry.list[0].view;
              const bad = entry.list.filter((r) => r.view?.needsAttention).length;
              return (
                <button key={`g-${entry.key}`} type="button" onClick={() => setCollapsed((c) => { const n = new Set(c); n.has(entry.key) ? n.delete(entry.key) : n.add(entry.key); return n; })}
                  style={{ all: 'unset', boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 20px', background: T.surface2, borderBottom: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 12.5, color: T.ink2 }}>
                  <ChevronRight size={14} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s', flexShrink: 0 }} />
                  <MessageSquare size={13} style={{ flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{first?.sessionTitle || 'Conversation'}</span>
                  <span style={{ color: T.ink4, flexShrink: 0 }}>{entry.list.length} run{entry.list.length === 1 ? '' : 's'}{bad ? ` · ${bad} ${bad === 1 ? 'needs' : 'need'} attention` : ''}</span>
                </button>
              );
            }
            const run = entry.run;
            const isSelected = selectedRun?._id === run._id;
            const isRunning  = run.status === 'running';
            const elapsed    = isRunning ? Date.now() - new Date(run.createdAt).getTime() : (run.duration || null);

            if (isMobile) {
              return (
                <div key={run._id} onClick={() => openRunDrawerFor(run)}
                  style={{ padding: '12px', paddingLeft: entry.indent ? 24 : 12, borderBottom: !last ? `1px solid ${T.border}` : 'none', cursor: 'pointer', background: isSelected ? T.accentLight : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <RunTitle run={run} isSelected={isSelected} />
                    <StatusBadge status={run.status} />
                  </div>
                  <div style={{ marginTop: 8 }}><ToolChips view={run.view} max={3} /><UsageLine view={run.view} /></div>
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: T.ink3 }}>
                      <HealthBadge view={run.view} status={run.status} />
                      <span>{timeAgo(run.createdAt)} · {formatDuration(elapsed)}</span>
                    </div>
                    {renderActions(run, true)}
                  </div>
                </div>
              );
            }

            return (
              <div key={run._id} onClick={() => openRunDrawerFor(run)}
                style={{
                  display: 'grid', gridTemplateColumns: ROW_COLS, gap: 12,
                  padding: '12px 20px', paddingLeft: entry.indent ? 38 : 20,
                  borderBottom: !last ? `1px solid ${T.border}` : 'none', alignItems: 'center', cursor: 'pointer',
                  background: isSelected ? T.accentLight : 'transparent',
                  borderLeft: `3px solid ${isSelected ? T.accent : 'transparent'}`, transition: 'background .12s',
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = T.surface2; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                <RunTitle run={run} isSelected={isSelected} />
                <div style={{ minWidth: 0 }}><ToolChips view={run.view} /><UsageLine view={run.view} /></div>
                <div><HealthBadge view={run.view} status={run.status} /></div>
                <div><StatusBadge status={run.status} /></div>
                <div style={{ fontSize: 13, color: T.ink3 }}>{timeAgo(run.createdAt)}</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: isRunning ? T.green : T.ink2 }}>{formatDuration(elapsed)}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{renderActions(run, false)}</div>
              </div>
            );
          })}
        </div>
        </>)}
      </div>

      {/* ── Drawer ── */}
      {view === 'Runs' && selectedRun && (
        <RunDrawer
          run={selectedRun}
          stopping={stopping}
          onStop={handleStop}
          onClose={() => setSelectedRun(null)}
        />
      )}
    </div>
  );
}
